import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { isEmpty, last, first } from 'lodash';

import Title from './title';

import money, { Money, format } from '../lib/money';
import { Time, TIME_FORMAT_COMPACT } from '../../components/Time';
import Purchase from '../lib/purchase';
import Membership from '../lib/membership';
import getCardTypeName from '../lib/card-type-names';
import { AUTHORIZED, CAPTURED } from '../lib/contribution-statuses';
import ElementWithPopover from './element-with-popover';
import PaymentSchedule from './payments-schedule';
import AvatarImg from '../../components/avatar-img';
import '../css/organizer-share-breakdown.css';

class OrganizerShareBreakdown extends Component {
  canRender() {
    return Membership.isCommitted(this.props.organizerMembership.status);
  }

  get contributionSchedule() {
    return this.props.contributionSchedule.map(contribution => ({
      status: contribution.status,
      amount: contribution.shareAmount,
      dueDeadline: (contribution.capturedAt || contribution.dueDeadline),
    }));
  }

  get currentContribution() {
    const { organizerMembership } = this.props;
    const contribution = organizerMembership.currentContribution;

    if (contribution) return contribution;
    return last(organizerMembership.contributions);
  }

  get collectedShareAmount() {
    if (this.paidContributions.length) {
      return Money.sum(this.paidContributions.map(c => money(c.shareAmount)));
    }
    return Money.zero(this.props.purchase.currencyCode);
  }

  get collectedAmount() {
    if (this.paidContributions.length) {
      return Money.sum(this.paidContributions.map(c => money(c.amount)));
    }
    return Money.zero(this.props.purchase.currencyCode);
  }

  get isRefund() {
    return this.isLastPayment && !this.refundableAmount.isZero();
  }

  get isSinglePayment() {
    return Purchase.isSinglePayment(this.props.purchase.payments);
  }

  get isLastPayment() {
    const { purchase: { payments, currentPayment } } = this.props;
    return !this.singlePayment && currentPayment.id === last(payments).id;
  }

  get paidContributions() {
    const { contributions } = this.props.organizerMembership;
    return (contributions || []).filter(c => c.status === CAPTURED || c.status === AUTHORIZED);
  }

  get shouldRenderHelperText() {
    const { purchase: { payments, currentPayment } } = this.props;
    return !this.isSinglePayment && currentPayment.id !== last(payments).id &&
      currentPayment.id !== first(payments).id;
  }

  get shouldRenderYourShareRow() {
    const { organizerMembership: { fees } } = this.props;
    return !money(fees.consumerFee).isZero() || !this.collectedShareAmount.isZero();
  }

  get shouldRenderFeeRow() {
    return !money(this.props.organizerMembership.fees.consumerFee).isZero();
  }

  get shouldRenderPaymentMadeRow() {
    return this.paidContributions.length > 0;
  }

  get renderingAnyRows() {
    return this.shouldRenderFeeRow || this.shouldRenderYourShareRow || this.shouldRenderPaymentMadeRow;
  }

  get refundableAmount() {
    return money(this.props.projectedMembership.refundableAmount);
  }

  get showSumLine() {
    return this.shouldRenderYourShareRow ||
      this.shouldRenderFeeRow || this.shouldRenderPaymentMadeRow;
  }

  get yourShareRowAmount() {
    if (this.isSinglePayment) return money(this.currentContribution.shareAmount);
    const sum = Money.sum([money(this.currentContribution.shareAmount), this.collectedShareAmount]);
    return Money.subtract(sum, this.refundableAmount);
  }

  headingTitle() {
    if (this.isSinglePayment) {
      return <FormattedMessage
        id="organizerShareBreakdown.title.singlePayment"
        defaultMessage="Your total charge"
      />;
    }

    if (this.isLastPayment && !this.refundableAmount.isZero()) {
      return <FormattedMessage
        id="organizerShareBreakdown.title.refund"
        defaultMessage="Your refund breakdown"
      />
    }

    return <FormattedMessage
      id="organizerShareBreakdown.title.multiPayment"
      defaultMessage="Your total charge today breakdown"
    />;
  }

  renderUserName() {
    const { user } = this.props;
    const name = !isEmpty(user.fullName) ? user.fullName : user.email;

    return (
      <div className="organizer-name">
        <h3 className="title bigger-title pb-2">
          <i className="fa fa-star fa-fw" aria-hidden="true" />
          {name}
        </h3>
        <span className="cool-grey">
          <FormattedMessage
            id="organizerShareBreakdown.organizer"
            defaultMessage="Organizer"
          />
        </span>
      </div>
    );
  }

  renderYourShareRow() {
    if (!this.shouldRenderYourShareRow) return;
    return (
      <div className="row">
        <div className="col-6">
          <span className="cool-grey">
            {this.selectYourShareRowCopy()}
          </span>
        </div>
        <div className="col-6 text-right">
          <span className="cool-grey">{format(this.yourShareRowAmount)}</span>
        </div>
      </div>
    );
  }

  selectYourShareRowCopy() {
    if (this.isSinglePayment) {
      return (
        <FormattedMessage
          id="organizerShareBreakdown.paymentBreakdown.singlePayment.totalShare"
          defaultMessage="Your share"
        />
      );
    }

    return (
      <FormattedMessage
        id="organizerShareBreakdown.paymentBreakdown.multiPayment.totalShare"
        defaultMessage="Your total share due to date"
      />
    );
  }

  renderPaymentMadeRows() {
    if (!this.shouldRenderPaymentMadeRow) return;
    const { contributions } = this.props.organizerMembership;
    const paidContributions = [];

    contributions.forEach((contribution) => {
      if (contribution.status === AUTHORIZED || contribution.status === CAPTURED) {
        paidContributions.push(this.renderSingleContribution(contribution));
      }
    });

    return paidContributions;
  }

  renderSingleContribution(contribution) {
    const date = <Time format={TIME_FORMAT_COMPACT} time={contribution.chargedAt} />;
    return (
      <div className="row" key={`contribution-${contribution.id}`}>
        <div className="col-6">
          <span className="cool-grey">
            <FormattedMessage
              id="organizerShareBreakdown.paymentMade"
              defaultMessage="Payment made ({date})"
              values={{
                date,
              }}
            />
          </span>
        </div>
        <div className="col-6 text-right">
          <span className="cool-grey">
            ({format(money(contribution.amount))})
          </span>
        </div>
      </div>
    );
  }

  renderFeeRow() {
    if (!this.shouldRenderFeeRow) return;

    const consumerFee = money(this.currentContribution.fees.consumerFee);
    return (
      <div className="row">
        <div className="col-6">
          <span className="cool-grey">
            <FormattedMessage
              id="organizerShareBreakdown.paymentBreakdown.transactionFee"
              defaultMessage="Your current payment transaction fee"
            />
          </span>
        </div>
        <div className="col-6 text-right">
          <span className="cool-grey">{format(consumerFee)}</span>
        </div>
      </div>
    );
  }

  renderPaymentBreakdown() {
    const className = `${this.renderingAnyRows ? 'share-breakdown' : ''}
      ${this.showSumLine ? ' divider' : ''}`;
    return (
      <div>
        <div className={className}>
          {this.renderYourShareRow()}
          {this.renderFeeRow()}
          {this.renderPaymentMadeRows()}
        </div>
        <div className="share-total row">
          <div className="col-8">
            {this.getBalanceCopy()}
          </div>
          <div className="col-4 text-right">
            {this.renderBalance()}
          </div>
          {this.renderHelperText()}
        </div>
      </div>
    );
  }

  renderHelperText() {
    if (!this.shouldRenderHelperText) return null;
    return (
      <div className="col-12 text-muted small-print">
        <FormattedMessage
          id="organizerShareBreakdown.helperText"
          defaultMessage="Any refunds due will be triggered only once the group’s last payment is made."
        />
      </div>
    )
  }

  getBalanceCopy() {
    const { cardType, lastFour } = this.props.organizerMembership.paymentSource.info;
    if (this.isRefund) {
      return (
        <FormattedMessage
          id="organizerShareBreakdown.paymentBreakdown.totalRefund"
          defaultMessage="Your refund to your {card} ending in {lastFour}"
          values={{
            card: getCardTypeName(cardType),
            lastFour
          }}
        />
      );
    }

    return (
      <FormattedMessage
        id="organizerShareBreakdown.paymentBreakdown.totalCharge"
        defaultMessage="Your total charge today to your {card} ending in {lastFour}"
        values={{
          card: getCardTypeName(cardType),
          lastFour
        }}
      />
    );
  }

  renderBalance() {
    if (this.isRefund) {
      return (
        <strong className="emerald-green">
          + {format(this.refundableAmount)}
          {this.renderPaymentSchedule()}
        </strong>
      );
    }

    return (
      <strong>
        {format(this.getBalanceAmount())}
        {this.renderPaymentSchedule()}
      </strong>
    );
  }

  getBalanceAmount() {
    if (this.isSinglePayment) return money(this.currentContribution.amount);
    return money(this.currentContribution.amount);
  }

  renderPaymentSchedule() {
    if (this.isSinglePayment || !this.props.contributionSchedule) return;
    const content = (
      <PaymentSchedule
        payments={this.contributionSchedule}
        totalAmount={this.props.projectedMembership.amount}
        refundableAmount={this.props.projectedMembership.refundableAmount}
        forMember
      />
    );

    return (
      <ElementWithPopover
        name="member-schedule"
        className="member-schedule-popover d-inline-block"
        content={content}
      >
        <i className="fa fa-fw fa-clock-o cool-grey" />
      </ElementWithPopover>
    );
  }

  render() {
    if (!this.canRender()) return null;

    const { user } = this.props;

    return (
      <div className="organizer-share-breakdown">
        <Title text={this.headingTitle()} name="organizer-share" divider bigger />
        <div className="organizer-card clearfix pt-4">
          <AvatarImg user={user} />
          {this.renderUserName()}
        </div>
        {this.renderPaymentBreakdown()}
      </div>
    );
  }
}

OrganizerShareBreakdown.defaultProps = {
  purchase: null,
  organizerMembership: null,
  projectedMembership: null,
  user: null,
};

export default OrganizerShareBreakdown;



// WEBPACK FOOTER //
// ./src/desktop/components/organizer-share-breakdown.js